import qs from 'query-string';
import {Filter, PaginationState, Shop} from 'interfaces';

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const ShopCache = new Map();
const BLACKLISTED_QUERY_KEYS = ['categoryId'];

export function filtersToQueryStringObject(filters: Filter[]) {
  return filters.reduce((query, filter) => {
    const options = filter.options
      .filter((option) => option.checked)
      .map((option) => option.optionId)
      .join(',');

    if (options) {
      query[filter.type] = options;
    }

    return query;
  }, {});
}

function getTruthyProperties(params) {
  Object.keys(params).forEach((param) => {
    if (!params[param]) {
      delete params[param];
    }
  });
  return params;
}

export interface GetShopsOptions {
  path: string;
  page?: number;
  search?: string;
  sortBy: string;
  filters: Filter[];
  categoryId?: string;
  locationIds?: string;
  queryToUrl?: boolean;
}

export interface ShopsResponse {
  shops: Shop[];
  filters: Filter[];
  pagination: PaginationState;
  nextPageURL: string;
  shopsCountString: string;
}

interface ShopCacheObject {
  opt: GetShopsOptions;
  url: string;
  shops: ShopsResponse;
  pathname?: string;
}

export type ShopsHistoryState = GetShopsOptions & ShopsResponse;

function serializeToHistory(opt, shops, pathname) {
  const historyState: ShopsHistoryState = {
    ...opt,
    ...shops,
  };

  history.pushState(historyState, document.title, pathname);
}

function removeKeys(queryObj) {
  return Object.entries(queryObj).reduce((memo, [key, value]) => {
    if (!BLACKLISTED_QUERY_KEYS.includes(key)) {
      memo[key] = value;
    }
    return memo;
  }, {});
}

export async function getShops(opt: GetShopsOptions): Promise<ShopsResponse | null> {
  const {path, filters, queryToUrl = false, ...params} = opt;
  const parsedQueryString = qs.parse(window.location.search) || {};
  let url = path;

  if (parsedQueryString.locationIds) {
    params.locationIds = parsedQueryString.locationIds;
  }

  const baseQuery = getTruthyProperties(params);
  const query = Object.assign(baseQuery, filtersToQueryStringObject(filters));
  const queryString = qs.stringify(query);

  if (queryString) {
    url += `?${queryString}`;
  }

  if (ShopCache.has(url)) {
    const {opt, shops, pathname} = ShopCache.get(url);

    if (pathname) {
      serializeToHistory(opt, shops, pathname);
    }

    return shops;
  }

  let shops;

  try {
    const response = await fetch(url, {headers});
    shops = await response.json();
  } catch (error) {
    return null;
  }

  const cacheObject: ShopCacheObject = {opt, url, shops};

  if (queryToUrl) {
    let pathname = location.pathname;
    const newQueryString = qs.stringify(removeKeys(query));
    pathname += `?${newQueryString}`;
    serializeToHistory(opt, shops, pathname);
    cacheObject.pathname = pathname;
  }

  ShopCache.set(url, cacheObject);
  return shops;
}
